import React, { useState, useMemo, useCallback } from 'react';
import { useAdmin } from '../../../../providers/AdminProvider';
import { useVetRec } from '../../../../providers/VetRecProvider';
import { EyeIcon } from '@heroicons/react/24/solid';
import VetRecButton from '../../../common/VetRecButton';
import { useUser } from '@clerk/clerk-react';
import AdminPicker from './AdminPicker';
import { getAlertBackgroundColor, getAlertIcon, getAlertIconColor } from '../../utils/adminUtils';
import Reminder from '../../../ReminderToast';

const GeneralSettings: React.FC = () => {
    const { superOrgMetadata, isImpersonation } = useVetRec()
    const { user } = useUser()
    const { users, updateSuperOrg, hideAlertBanner, alertBanner, alertMessage, alertTitle, alertType } = useAdmin()
    const [name, setName] = useState<string>(superOrgMetadata?.name || "")
    const [admins, setAdmins] = useState<string[]>(superOrgMetadata?.owner)
    const [tier, setTier] = useState<string>(superOrgMetadata?.tier || "")
    const [domain, setDomain] = useState<string>(superOrgMetadata?.domain || "")
    const [supportTier, setSupportTier] = useState<string>(superOrgMetadata?.support_tier || "")

    const filteredUsers = useMemo(() => {
        return users.filter((user) => user.name !== "" && user.name !== undefined && user.name !== null && !user.id.includes("orginv_"))
    }, [users])

    const unsavedChanges = useMemo(() => {
        const superOrgOwners: string[] = Array.isArray(superOrgMetadata?.owner) ? superOrgMetadata.owner as string[] : [];
        return admins.slice().sort().join(',') !== superOrgOwners.slice().sort().join(',') ||
            name !== (superOrgMetadata?.name || "") ||
            tier !== (superOrgMetadata?.tier || "") ||
            domain !== (superOrgMetadata?.domain || "") ||
            supportTier !== (superOrgMetadata?.support_tier || "")
    }, [admins, domain, name, superOrgMetadata?.domain, superOrgMetadata?.name, superOrgMetadata?.owner, superOrgMetadata?.support_tier, superOrgMetadata?.tier, supportTier, tier])

    const getUsersForAdmins = useCallback((admins: string[]) => {
        return filteredUsers.filter((user) => admins.includes(user.email))
    }, [filteredUsers])

    const handleUpdateSuperAdmins = (selected: string[]) => {
        setAdmins(selected)
    }

    const handleRemoveAdmin = (admin: string) => {
        setAdmins((prev) => prev.filter((a) => a !== admin))
    }

    const saveOrganizationSettings = async () => {
        await updateSuperOrg(name, getUsersForAdmins(admins), tier, domain, supportTier)
    }

    const currentUserEmails = useMemo(() => {
        return user?.emailAddresses.map((email) => email.emailAddress) || []
    }, [user])

    return (
        <div className="mt-8 p-4 flex flex-col space-y-6">
            <Reminder show={alertBanner} hide={hideAlertBanner} text={alertMessage} title={alertTitle} iconColor={getAlertIconColor(alertType)} icon={getAlertIcon(alertType)} lineColor={getAlertBackgroundColor(alertType)} />
            <div className='flex flex-row justify-between items-center'>
                <div className='flex flex-col space-y-2'>
                    <h2 className="text-xl font-bold">Organization Settings</h2>
                </div>
                <VetRecButton onClick={saveOrganizationSettings} className={`inline-flex items-center justify-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white shadow-sm ${!unsavedChanges || admins.length === 0 ? "bg-gray-300" : "bg-blue-600 hover:bg-blue-700"} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:w-auto h-10`} disabled={!unsavedChanges || admins.length === 0}>
                    Save
                </VetRecButton>
            </div>
            <div className='flex flex-col space-y-2'>
                <h3 className="text-md font-semibold text-gray-700">
                    Organization Name
                </h3>
                <input className="border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" type="text" value={name} onChange={(event) => setName(event.target.value)}/>
            </div>
            <div className='flex flex-col space-y-2'>
                <h3 className="text-md font-semibold text-gray-700">
                    Organization Administrators
                </h3>
                <AdminPicker 
                    admins={admins}
                    currentUserEmails={currentUserEmails}
                    filteredUsers={filteredUsers}
                    handleUpdateSuperAdmins={handleUpdateSuperAdmins}
                    handleRemoveAdmin={handleRemoveAdmin}
                />   
            </div>
            {isImpersonation && <div className='flex flex-col space-y-4 border border-pink-600 rounded-md p-4 border-dashed'>
                <div className='flex flex-row gap-x-2 items-center font-semibold text-gray-800'>Internal User Only <EyeIcon className='text-pink-600 h-5 w-5'/></div>
                <div className='flex flex-col space-y-2'>
                    <h3 className="text-md font-semibold text-gray-700">
                        Super Org Tier
                    </h3>
                    <input className="border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" type="text" value={superOrgMetadata?.tier} onChange={(event) => setTier(event.target.value)} />
                </div>
                <div className='flex flex-col space-y-2'>
                    <h3 className="text-md font-semibold text-gray-700">
                        Super Org Domain
                    </h3>
                    <input className="border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" type="text" value={superOrgMetadata?.domain} onChange={(event) => setDomain(event.target.value)} />
                </div>
                <div className='flex flex-col space-y-2'>
                    <h3 className="text-md font-semibold text-gray-700">
                        Super Org Support Tier
                    </h3>
                    <input className="border border-gray-300 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" type="text" value={superOrgMetadata?.support_tier} onChange={(event) => setSupportTier(event.target.value)} />
                </div>
            </div>}
        </div>
    );
};

export default GeneralSettings;